import React, { useEffect } from "react";
import SignaturesTab from "../../components/SignatureTab";
import { Permit, getPermitGeofencing } from "../../models/Permit";
import { AppState } from "../../store";
import { connect } from "react-redux";
import { submitPermitAuthorisation } from "./actions";
import { User } from "../../models/User";
import { Guid } from "guid-typescript";
import PermitFlagService, { GreenFlag } from "../../PermitFlagService";
import { RawFile } from "../../models/RawFile";
import { PermitAuthorisationSignature } from "../../models/PermitAuthorisationSignature";
import { Role, getPermitRoleSettings } from "../../models/Role";
import PermissionService from "../../PermissionService";
import { ISSUE_PERMIT, READY_TO_ISSUE } from "../../constants";
import { updatePermitLastAccess } from "../PermitContainer/thunks";
import { GetPositionOptions, LocationErrorCode } from "../../LocationService";
import LocationService from "../../LocationService";

interface SignaturesTabContainerProps {
    permit: Permit | null;
    permitGeofenceCheckResult: {
        isWithinGeofence: boolean;
        error: string | null;
        errorCode: LocationErrorCode | null;
    } | null;
    clientRoles: Role[];
    currentUser: User | null;
    permitRegisterUsers: User[];
    submitPermitAuthorisation: typeof submitPermitAuthorisation;
    updatePermitLastAccess: typeof updatePermitLastAccess;
    isReadOnly: boolean;
}

const SignaturesTabContainer: React.FC<SignaturesTabContainerProps> = ({
    permit,
    permitGeofenceCheckResult,
    clientRoles,
    currentUser,
    permitRegisterUsers,
    submitPermitAuthorisation,
    updatePermitLastAccess,
    isReadOnly,
}) => {
    const requestUpdatePermitLastAccess = (options?: GetPositionOptions) => {
        if (permit && currentUser) {
            updatePermitLastAccess(permit, currentUser.id, options);
        }
    };

    useEffect(() => {
        requestUpdatePermitLastAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permit?.id, currentUser?.id]);

    const handleRefreshButtonClicked = async () => {
        if (permitGeofenceCheckResult?.errorCode === LocationErrorCode.PERMISSION_DENIED) {
            await LocationService.requestPermissions().catch(() => {});
        }
        requestUpdatePermitLastAccess({ maximumAge: 0 });
    };

    const generateFileKey = (): string => {
        return "file-" + Guid.create().toString();
    };

    const handlePermitAssignmentSignatureSubmitted = async (
        signature: PermitAuthorisationSignature,
        signatureDataUrl: string
    ) => {
        const fileKey = generateFileKey();

        const coordinates =
            permit?.lastAccess?.latitude && permit?.lastAccess?.longitude
                ? { latitude: permit.lastAccess.latitude, longitude: permit.lastAccess.longitude }
                : undefined;

        currentUser &&
            submitPermitAuthorisation(
                {
                    ...signature,
                    dateSigned: new Date(),
                    signatureFileKey: fileKey,
                    coordinates: coordinates,
                } as PermitAuthorisationSignature,
                { fileName: fileKey, contentType: "image/png", dataUrl: signatureDataUrl } as RawFile,
                clientRoles,
                currentUser.id
            );
    };

    const assigneesWhoShouldSign = permit?.permitAssignments?.filter((a) => {
        const role = clientRoles.find((c) => c.id === a.roleId);
        return role ? getPermitRoleSettings(role, permit?.permitType?.id).shouldAuthorizePermit : false;
    });

    if (!permit || !currentUser) return null;
    const ableToIssuePermit = PermissionService.userHasPermission(
        currentUser,
        ISSUE_PERMIT,
        clientRoles,
        permit.permitRegister!.id,
        permit.permitType!.id
    );

    const isPermitFlagForAuthorisationGreen = PermitFlagService.getPermitFlagForAuthorisation(permit) === GreenFlag;
    const isPermitStatusReadyToIssue = permit.status === READY_TO_ISSUE;
    const geofencing = getPermitGeofencing(permit);
    const abilityToSign = !isPermitStatusReadyToIssue
        ? true
        : isPermitFlagForAuthorisationGreen &&
          (!((geofencing?.status ?? "disabled") === "enforced") ||
              (permitGeofenceCheckResult?.isWithinGeofence ?? false));

    return (
        <SignaturesTab
            permitId={permit.id}
            permitTypeId={permit?.permitType?.id}
            assigneesWhoShouldSign={assigneesWhoShouldSign}
            signatures={permit.signatures}
            clientRoles={clientRoles}
            permitRegisterUsers={permitRegisterUsers}
            onPermitAssignmentSignatureSubmitted={handlePermitAssignmentSignatureSubmitted}
            onRefreshButtonClicked={handleRefreshButtonClicked}
            currentUser={currentUser}
            abilityToSign={abilityToSign}
            isReadOnly={!ableToIssuePermit}
            shouldShowCompletePreviousTabsError={!isPermitFlagForAuthorisationGreen}
            shouldShowGeofencingError={
                isPermitStatusReadyToIssue &&
                (geofencing?.status ?? "disabled") !== "disabled" &&
                !permitGeofenceCheckResult?.isWithinGeofence
            }
            geofencingError={permitGeofenceCheckResult?.error ?? null}
        />
    );
};

function mapStateToProps(state: AppState) {
    return {
        permit: state.permitPage.permit,
        permitGeofenceCheckResult: state.permitPage.permitGeofenceCheckResult,
        currentUser: state.user.currentUser,
        clientRoles: state.myPermits.clientRoles,
        permitRegisterUsers: state.permitAssignments.permitRegisterUsers,
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        submitPermitAuthorisation: (
            authorisation: PermitAuthorisationSignature,
            signatureFile: RawFile,
            clientRoles: Role[],
            loggedInUserId: string
        ) => dispatch(submitPermitAuthorisation(authorisation, signatureFile, clientRoles, loggedInUserId)),
        updatePermitLastAccess: (permit: Permit, userId: string, options?: GetPositionOptions) =>
            dispatch(updatePermitLastAccess(permit, userId, options)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SignaturesTabContainer);
